import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { CapabilityStatus } from '@hooks/useCapabilities';

interface CapabilityDisclosureProps {
  status: CapabilityStatus;
  /** False when the section exists but this caller is not entitled to it. */
  hasValues: boolean;
  titleKey: string;
  onRetry: () => void;
  children: React.ReactNode;
}

/** Shared shell for the "what applies to you" panels. Non-blocking, keyboard-reachable. */
const CapabilityDisclosure: React.FC<CapabilityDisclosureProps> = ({
  status, hasValues, titleKey, onRetry, children,
}) => {
  const { t } = useTranslation();

  if (status === 'loading') {
    return <p className="text-xs text-muted-foreground">{t('upload.limitsLoading')}</p>;
  }

  if (status === 'error' || !hasValues) {
    return (
      <Alert>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>{t('upload.limitsUnavailable')}</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="capability-limits">
        <AccordionTrigger className="text-sm">{t(titleKey)}</AccordionTrigger>
        <AccordionContent>
          <ul className="space-y-1 text-sm text-muted-foreground">{children}</ul>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default CapabilityDisclosure;
